import React from 'react'
import { Link } from 'react-router-dom'
import { CheckCircleIcon, ExclamationTriangleIcon } from '@heroicons/react/24/outline'

/** Wspólna ramka wszystkich ekranów kont — jeden układ, jedno miejsce na zmiany.
 *
 * `szeroki` jest dla rejestracji: mieści opcjonalne kafelki typu działalności
 * w dwóch kolumnach. W `max-w-md` schodziły do jednej i sekcja rozciągała się
 * na pół ekranu w pionie. Pozostałe ekrany (logowanie, reset hasła) zostają
 * wąskie — trzy pola rozciągnięte na szeroką kartę wyglądają na zgubione.
 */
export const AuthLayout = ({ title, subtitle, children, footer, szeroki }) => (
  <div className="px-4 py-10 sm:py-16 flex justify-center">
    <div className={`w-full ${szeroki ? 'max-w-xl' : 'max-w-md'}`}>
      <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-6 sm:p-8">
        <h1 className="text-2xl font-bold text-gray-900">{title}</h1>
        {subtitle && <p className="mt-2 text-sm text-gray-500 leading-6">{subtitle}</p>}
        <div className="mt-6">{children}</div>
      </div>
      {footer && <div className="mt-5 text-center text-sm text-gray-500">{footer}</div>}
    </div>
  </div>
)

export const Field = ({ label, hint, error, children }) => (
  <label className="block mb-4">
    <span className="block text-sm font-medium text-gray-700 mb-1.5">{label}</span>
    {children}
    {hint && !error && <span className="block mt-1 text-xs text-gray-400">{hint}</span>}
    {error && <span className="block mt-1 text-xs text-red-600">{error}</span>}
  </label>
)

export const inputClass =
  'w-full px-3.5 py-2.5 rounded-lg border border-gray-200 bg-white text-sm ' +
  'focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent ' +
  'disabled:bg-gray-50 disabled:text-gray-400'

export const SubmitButton = ({ children, busy, disabled }) => (
  <button
    type="submit"
    disabled={busy || disabled}
    className="w-full px-4 py-2.5 rounded-lg bg-blue-600 text-white text-sm font-semibold
               hover:bg-blue-500 active:bg-blue-700 transition-colors shadow-sm
               disabled:bg-gray-300 disabled:cursor-not-allowed"
  >
    {busy ? 'Chwileczkę…' : children}
  </button>
)

/** Komunikat błędu. Zawsze widoczny i konkretny — „coś poszło nie tak" nie pomaga. */
export const ErrorBox = ({ children }) =>
  children ? (
    <div role="alert" className="mb-4 flex gap-2.5 rounded-lg bg-red-50 border border-red-100 p-3">
      <ExclamationTriangleIcon className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />
      <p className="text-sm text-red-700 leading-6">{children}</p>
    </div>
  ) : null

export const SuccessBox = ({ children }) =>
  children ? (
    <div className="mb-4 flex gap-2.5 rounded-lg bg-emerald-50 border border-emerald-100 p-3">
      <CheckCircleIcon className="w-5 h-5 text-emerald-600 flex-shrink-0 mt-0.5" />
      <div className="text-sm text-emerald-800 leading-6">{children}</div>
    </div>
  ) : null

export const AuthLink = ({ to, state, children }) => (
  <Link to={to} state={state} className="text-blue-600 font-medium hover:underline">{children}</Link>
)

export default AuthLayout
